import { batch50Timelines, nextBatch50Timelines } from './castBatch50.ts'
import { researchedTimelinesB } from './castBios2021B.ts'
import { researchedTimelinesA } from './castBios2021A.ts'
import { researchedTimelinesC } from './castBios2021C.ts'
import { editorialOrigins } from './castProfileOrigins.ts'
import { legacyTimelinesB } from './castBiosLegacyB.ts'
import { legacyTimelinesA } from './castBiosLegacyA.ts'
import { legacyTimelinesC } from './castBiosLegacyC.ts'
import { researchedTimelinesCFollowup } from './castBios2021CFollowup.ts'
import { finalCarmellaTimelines } from './castBiosFinalCarmella.ts'
import { finalIdentityTimelines } from './castBiosFinalIdentities.ts'
import { finalHuntTimelinesCE } from './castBiosFinalHuntCE.ts'
import { finalHuntTimelinesCT } from './castBiosFinalHuntCT.ts'
import { familyChantelCanonTimelines, familyChantelSeasonAdditions } from './castBiosFamilyChantelCanon.ts'
import { darceyStaceyCanonTimelines, darceyStaceySeasonAdditions } from './castBiosDarceyStaceyCanon.ts'
import { canonCastNames } from './canonCastNames.ts'

export type CastConnection = {
  name: string
  personId?: string
  relationshipId?: string
  context: string
}

export type CastSeasonChapter = {
  show: string
  season: number
  title: string
  recap: string
  status?: 'aired' | 'announced'
  partnerPersonIds?: string[]
  relationshipId?: string
}

export type CastProfileTimeline = {
  origin?: {
    partnerName: string
    partnerPersonId?: string
    introLabel?: string
    show: string
    season: number
    connectionKind?: 'romantic' | 'family' | 'friend' | 'unverified-identity' | 'independent'
  }
  connections: CastConnection[]
  seasons: CastSeasonChapter[]
}

const B90 = '90 Day Fiancé: Before the 90 Days'
const MAIN = '90 Day Fiancé'
const HEA = '90 Day Fiancé: Happily Ever After?'
const TOW = '90 Day Fiancé: The Other Way'
const LAST_RESORT = '90 Day: The Last Resort'
const SINGLE_LIFE = '90 Day: The Single Life'
const HUNT_FOR_LOVE = '90 Day: Hunt for Love'

const coreCastProfileTimelines: Record<string, CastProfileTimeline> = {
  'p-jasmine-pineda': {
    connections: [
      {
        name: 'Gino Palazzolo',
        personId: 'p-gino-palazzolo',
        relationshipId: 'ARC-B09532768188',
        context: 'Her original online match, later fiancé and husband, now ex-husband following their confirmed June 2026 divorce.',
      },
      {
        name: 'Matt Branis',
        context: 'The later partner introduced during the open-marriage chapter and the father of Jasmine’s daughter, Matilda.',
      },
    ],
    seasons: [
      {
        show: B90,
        season: 5,
        title: 'First meeting in Panama',
        recap: 'After eight months online, Gino traveled to Panama to meet Jasmine in person. Jealousy, money and trust collided almost immediately, but the trip still ended with a proposal.',
        partnerPersonIds: ['p-gino-palazzolo'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: B90,
        season: 6,
        title: 'An engagement under pressure',
        recap: 'The couple tried to prepare for marriage while arguing over a prenup, finances and old relationships. Their K-1 plans moved forward, even when their trust seemed to be moving in the opposite direction.',
        partnerPersonIds: ['p-gino-palazzolo'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: MAIN,
        season: 10,
        title: 'Panama to Michigan',
        recap: 'Jasmine arrived in Michigan for the 90-day countdown and discovered that a new country did not magically retire their old arguments. They nevertheless reached the altar and married.',
        partnerPersonIds: ['p-gino-palazzolo'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: HEA,
        season: 8,
        title: 'Marriage, paperwork and a pageant',
        recap: 'Married life brought fights over intimacy, money and the immigration process for Jasmine’s sons. A beauty-pageant storyline provided sparkle, but not a solution to the larger problems.',
        partnerPersonIds: ['p-gino-palazzolo'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: LAST_RESORT,
        season: 2,
        title: 'One last retreat—and an open door',
        recap: 'Gino and Jasmine entered couples therapy with their marriage in serious trouble. The retreat ended with an open-marriage experiment that brought Matt into the story and ultimately did not save the marriage.',
        partnerPersonIds: ['p-gino-palazzolo'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: HEA,
        season: 9,
        title: 'The open marriage changes everything',
        recap: 'Back home, Gino and Jasmine discovered that additional partners made their already-fragile marriage even harder to manage. Jasmine’s relationship with Matt became a new family chapter while she and Gino moved toward divorce.',
        partnerPersonIds: ['p-gino-palazzolo'],
        relationshipId: 'ARC-B09532768188',
      },
    ],
  },
  'p-yara-zaya': {
    connections: [
      {
        name: 'Jovi Dufren',
        personId: 'p-jovi-dufren',
        relationshipId: 'ARC-55676CA1337A',
        context: 'Her travel-app match, husband and co-parent throughout her franchise story.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 8,
        title: 'A K-1 move to New Orleans',
        recap: 'Yara moved from Kyiv to Louisiana and quickly learned that Jovi’s work travel and nightlife would be recurring cast members. A surprise pregnancy accelerated the wedding plans and the start of their family.',
        partnerPersonIds: ['p-jovi-dufren'],
        relationshipId: 'ARC-55676CA1337A',
      },
      {
        show: HEA,
        season: 6,
        title: 'New parents, familiar disagreements',
        recap: 'With baby Mylah at the center of their lives, Yara wanted stability while Jovi struggled to retire his bachelor habits. Work absences and one especially unhelpful strip-club outing kept the peace elusive.',
        partnerPersonIds: ['p-jovi-dufren'],
        relationshipId: 'ARC-55676CA1337A',
      },
      {
        show: HEA,
        season: 7,
        title: 'Europe enters the family plan',
        recap: 'A trip to Europe during the war in Ukraine intensified Yara’s homesickness and her wish for a European home base. Jovi worried that a second residence could become a second life without him.',
        partnerPersonIds: ['p-jovi-dufren'],
        relationshipId: 'ARC-55676CA1337A',
      },
      {
        show: LAST_RESORT,
        season: 1,
        title: 'The baby question reaches therapy',
        recap: 'The couple entered the retreat divided over trust, nightlife and whether to have another child. Yara’s private use of birth control became a major conversation about autonomy and communication.',
        partnerPersonIds: ['p-jovi-dufren'],
        relationshipId: 'ARC-55676CA1337A',
      },
      {
        show: HEA,
        season: 9,
        title: 'A Miami move and new friendships',
        recap: 'Jovi and Yara relocated to Miami with Mylah and tried to turn the change into a fresh start. Their new social circle with other franchise couples brought support, friction and several opinions nobody had ordered.',
        partnerPersonIds: ['p-jovi-dufren'],
        relationshipId: 'ARC-55676CA1337A',
      },
    ],
  },
  'p-paola-mayfield': {
    connections: [
      {
        name: 'Russ Mayfield',
        personId: 'p-russ-mayfield',
        relationshipId: 'ARC-6C7FAE6C41EF',
        context: 'The engineer she met in Colombia, later married and built a family with in the United States.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 1,
        title: 'Colombia meets Oklahoma',
        recap: 'Pao arrived on a K-1 visa and tried to picture a future in Russ’s conservative Oklahoma hometown. The couple made it through culture shock and family concerns to become one of the franchise’s original marriages.',
        partnerPersonIds: ['p-russ-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
      {
        show: HEA,
        season: 1,
        title: 'Miami calls',
        recap: 'Pao pursued modeling opportunities in Miami while Russ worried about the jobs, photoshoots and distance involved. Their first post-wedding season established geography as a permanent third wheel.',
        partnerPersonIds: ['p-russ-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
      {
        show: HEA,
        season: 2,
        title: 'Career dreams versus comfort zones',
        recap: 'The move toward Miami gave Pao more room to chase her ambitions and Russ more reasons to feel displaced. They tried to support each other while disagreeing over work, friends and boundaries.',
        partnerPersonIds: ['p-russ-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
      {
        show: HEA,
        season: 3,
        title: 'Grief changes the conversation',
        recap: 'After suffering a miscarriage, Pao and Russ worked through grief alongside their familiar communication problems. The season shifted their family plans from an abstract debate to an emotional priority.',
        partnerPersonIds: ['p-russ-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
      {
        show: HEA,
        season: 4,
        title: 'Becoming parents',
        recap: 'Pregnancy and the birth of their son Axel opened a joyful new chapter, but family boundaries and differing expectations followed them into parenthood. Even the baby shower came with a side of tension.',
        partnerPersonIds: ['p-russ-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
      {
        show: LAST_RESORT,
        season: 3,
        title: 'A marriage living in two places',
        recap: 'Pao’s wrestling career took her to Las Vegas while Russ resisted being left behind, widening a separation that was already emotional. At the castle, old resentments and painful revelations forced them to ask whether the marriage could continue.',
        partnerPersonIds: ['p-russ-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
    ],
  },
  'p-rebecca-parrott': {
    connections: [
      {
        name: 'Zied Hakimi',
        personId: 'p-zied-hakimi',
        relationshipId: 'ARC-3A90E6C05154',
        context: 'Her online match from Tunisia, later fiancé and husband in the United States.',
      },
    ],
    seasons: [
      {
        show: B90,
        season: 3,
        title: 'From filtered selfies to Tunisia',
        recap: 'Rebecca traveled to Tunisia to meet Zied and decide whether their online romance could become an engagement. Revelations about her past marriage and their age gap raised the stakes before Zied proposed.',
        partnerPersonIds: ['p-zied-hakimi'],
        relationshipId: 'ARC-3A90E6C05154',
      },
      {
        show: MAIN,
        season: 8,
        title: 'Zied comes to Georgia',
        recap: 'Zied arrived on the K-1 visa and moved into Rebecca’s Georgia life, complete with skeptical relatives and an unconventional temporary home. Ramadan timing turned the wedding calendar into its own emergency.',
        partnerPersonIds: ['p-zied-hakimi'],
        relationshipId: 'ARC-3A90E6C05154',
      },
      {
        show: LAST_RESORT,
        season: 3,
        title: 'Seven years, one final chance',
        recap: 'Years of cultural differences, distance and cheating accusations had left Rebecca and Zied largely living separate lives under one roof. The retreat pushed both of them to decide whether reconciliation was still realistic.',
        partnerPersonIds: ['p-zied-hakimi'],
        relationshipId: 'ARC-3A90E6C05154',
      },
    ],
  },
  'p-sophie-sierra': {
    connections: [
      {
        name: 'Rob Warne',
        personId: 'p-rob-warne',
        relationshipId: 'ARC-6E9479AA5D80',
        context: 'Her original social-media match, fiancé and husband after moving to Los Angeles.',
      },
      {
        name: 'Pedro Jimeno',
        personId: 'p-pedro-jimeno',
        relationshipId: 'ARC-06EDA993968C',
        context: 'The later franchise connection she explored while starting over on The Single Life.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 10,
        title: 'A very unglamorous Los Angeles landing',
        recap: 'Sophie moved from London to marry Rob and quickly found that his Los Angeles setup did not match the life she imagined. Online cheating, money and trust followed them to the altar anyway.',
        partnerPersonIds: ['p-rob-warne'],
        relationshipId: 'ARC-6E9479AA5D80',
      },
      {
        show: HEA,
        season: 8,
        title: 'Married, but mostly apart',
        recap: 'Sophie spent much of the season living away from Rob while they tried to repair trust and intimacy. Therapy and increasingly creative date ideas could not settle the larger question of whether the marriage was working.',
        partnerPersonIds: ['p-rob-warne'],
        relationshipId: 'ARC-6E9479AA5D80',
      },
      {
        show: LAST_RESORT,
        season: 2,
        title: 'The retreat becomes the ending',
        recap: 'Rob and Sophie entered therapy with years of hurt and suspicion already packed in their bags. They briefly reconnected after the retreat, but ultimately ended the relationship for good.',
        partnerPersonIds: ['p-rob-warne'],
        relationshipId: 'ARC-6E9479AA5D80',
      },
      {
        show: SINGLE_LIFE,
        season: 5,
        title: 'A new start—and a Pedro crossover',
        recap: 'After filing for divorce, Sophie returned to dating and met Pedro during a trip to the Dominican Republic. Their chemistry was obvious; their definitions of exclusivity were considerably less synchronized.',
        partnerPersonIds: ['p-pedro-jimeno'],
        relationshipId: 'ARC-06EDA993968C',
      },
    ],
  },
  'p-chantel-everett': {
    connections: [
      {
        name: 'Pedro Jimeno',
        personId: 'p-pedro-jimeno',
        relationshipId: 'ARC-35AAB4B45C12',
        context: 'Her Spanish-practice partner, fiancé and husband before their eventual divorce.',
      },
      {
        name: 'Giannis',
        personId: 'p-giannis-chantel-arc',
        relationshipId: 'ARC-1B2F99EAC8E9',
        context: 'The Greek soccer player she dated during her first post-divorce season.',
      },
      {
        name: 'Ashley Bowen',
        context: 'A Hunt for Love friendship that became a relationship and later an engagement on 90 Day Diaries.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 4,
        title: 'The secret fiancé arrives',
        recap: 'Pedro came to Atlanta on a K-1 visa while Chantel initially told her family that he was there as a student. The truth surfaced, the families braced for impact and the couple married.',
        partnerPersonIds: ['p-pedro-jimeno'],
        relationshipId: 'ARC-35AAB4B45C12',
      },
      {
        show: HEA,
        season: 2,
        title: 'Two families enter the marriage',
        recap: 'Chantel and Pedro tried to settle into married life while suspicion grew on both sides of the family. Questions about money, motives and loyalty turned nearly every gathering into a summit.',
        partnerPersonIds: ['p-pedro-jimeno'],
        relationshipId: 'ARC-35AAB4B45C12',
      },
      {
        show: HEA,
        season: 3,
        title: 'The family feud goes international',
        recap: 'Conflict between Pedro and Chantel’s relatives escalated into one of the franchise’s most memorable dinner-table disasters. Trips between Atlanta and the Dominican Republic did little to lower the temperature.',
        partnerPersonIds: ['p-pedro-jimeno'],
        relationshipId: 'ARC-35AAB4B45C12',
      },
      {
        show: HEA,
        season: 4,
        title: 'Distance and suspicion take over',
        recap: 'Pedro spent more time in the Dominican Republic while Chantel questioned his priorities and friendships. Their marriage continued, but the trust gap widened enough to launch an entire family spinoff.',
        partnerPersonIds: ['p-pedro-jimeno'],
        relationshipId: 'ARC-35AAB4B45C12',
      },
      {
        show: SINGLE_LIFE,
        season: 4,
        title: 'Dating again in Greece',
        recap: 'Newly divorced Chantel took a friends’ trip to Greece and pursued a connection with soccer player Giannis. Vacation chemistry met the practical problem of living on opposite sides of the Atlantic.',
        partnerPersonIds: ['p-giannis-chantel-arc'],
        relationshipId: 'ARC-1B2F99EAC8E9',
      },
      {
        show: HUNT_FOR_LOVE,
        season: 1,
        title: 'The singles retreat opens a new door',
        recap: 'Chantel arrived in Tulum ready to date again after Pedro and explored the retreat’s mix of new and familiar faces. Her friendship with Ashley became the connection that continued after filming.',
      },
    ],
  },
  'p-thais-ramone': {
    connections: [
      {
        name: 'Patrick Mendes',
        personId: 'p-patrick-mendes',
        relationshipId: 'ARC-B15A1B3EFFC7',
        context: 'The dating-app match she moved from Brazil to marry and later welcomed a daughter with.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 9,
        title: 'Portuguese lessons become a K-1 story',
        recap: 'Patrick met Thaís while looking for help with Portuguese, then brought her to Texas on a K-1 visa. Her father’s disapproval and Patrick’s brother John made the countdown unusually crowded, but the couple married.',
        partnerPersonIds: ['p-patrick-mendes'],
        relationshipId: 'ARC-B15A1B3EFFC7',
      },
      {
        show: HEA,
        season: 8,
        title: 'Back to Brazil as a family',
        recap: 'Patrick and Thaís returned to Brazil with their daughter so she could meet the family, especially Thaís’s father Carlos. The emotional reunion did not erase the unfinished tension between Carlos and Patrick.',
        partnerPersonIds: ['p-patrick-mendes'],
        relationshipId: 'ARC-B15A1B3EFFC7',
      },
      {
        show: LAST_RESORT,
        season: 3,
        title: 'Money, intimacy and family grief',
        recap: 'A failed business, health problems, family tragedy and financial pressure had left the couple disconnected. Therapy exposed sharp disagreements about spending, sex, travel to Brazil and whether either partner still felt supported.',
        partnerPersonIds: ['p-patrick-mendes'],
        relationshipId: 'ARC-B15A1B3EFFC7',
      },
    ],
  },
  'p-ariela-weinberg': {
    connections: [
      {
        name: 'Biniyam Shibre',
        personId: 'p-biniyam-shibre',
        relationshipId: 'ARC-84F46727A2A5',
        context: 'The Ethiopian performer she met while traveling and later built an international family with.',
      },
    ],
    seasons: [
      {
        show: TOW,
        season: 2,
        title: 'A baby and a move to Ethiopia',
        recap: 'Pregnant Ari returned to Ethiopia to give birth near Biniyam and attempt a shared life. Housing, religion and trust became immediate tests as they welcomed their son Avi.',
        partnerPersonIds: ['p-biniyam-shibre'],
        relationshipId: 'ARC-84F46727A2A5',
      },
      {
        show: TOW,
        season: 3,
        title: 'Distance tests the engagement',
        recap: 'Ari brought Avi to the United States for surgery while Bini remained in Ethiopia. Reports of parties and unanswered calls deepened their trust problems before the couple pursued a move to America.',
        partnerPersonIds: ['p-biniyam-shibre'],
        relationshipId: 'ARC-84F46727A2A5',
      },
      {
        show: MAIN,
        season: 9,
        title: 'Starting over in New Jersey',
        recap: 'Biniyam arrived in the United States and chased an MMA dream while Ari worried about finances and stability. Ex-husband Leandro remained in their orbit, but Ari and Bini reached the wedding.',
        partnerPersonIds: ['p-biniyam-shibre'],
        relationshipId: 'ARC-84F46727A2A5',
      },
      {
        show: LAST_RESORT,
        season: 2,
        title: 'A separated couple tries therapy',
        recap: 'Ari and Bini arrived separately with their marriage already deeply fractured. The retreat forced a painful conversation about trust, distance and whether divorce and co-parenting were now the healthiest path.',
        partnerPersonIds: ['p-biniyam-shibre'],
        relationshipId: 'ARC-84F46727A2A5',
      },
    ],
  },
  'p-elizabeth-potthast': {
    connections: [
      {
        name: 'Andrei Castravet',
        personId: 'p-andrei-castravet',
        relationshipId: 'ARC-7A6D723EF5C6',
        context: 'The Moldovan man she met in Dublin, later married and built a family and business with in Florida.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 5,
        title: 'Dublin romance, Florida reality',
        recap: 'Andrei moved to Florida on a K-1 visa after meeting Elizabeth in Dublin. Her family questioned his plans and personality throughout the countdown, but the couple married.',
        partnerPersonIds: ['p-andrei-castravet'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
      {
        show: HEA,
        season: 4,
        title: 'Marriage meets the family business',
        recap: 'Money, work and Andrei’s relationship with Elizabeth’s relatives dominated married life as they prepared for their first child. Everyone had a job opinion, including several people who were not hiring.',
        partnerPersonIds: ['p-andrei-castravet'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
      {
        show: HEA,
        season: 5,
        title: 'A second wedding in Moldova',
        recap: 'The couple planned a traditional Moldovan wedding while Elizabeth’s family investigated Andrei’s past and clashed with his culture. The celebration happened, but diplomacy did not attend every event.',
        partnerPersonIds: ['p-andrei-castravet'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
      {
        show: HEA,
        season: 6,
        title: 'Real estate becomes a family sport',
        recap: 'Andrei joined Chuck’s real-estate business, inflaming old tensions with Elizabeth’s siblings over money and opportunity. A family cookout proved that property was not the only thing capable of flipping.',
        partnerPersonIds: ['p-andrei-castravet'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
      {
        show: HEA,
        season: 7,
        title: 'Immigration questions and family suspicion',
        recap: 'Andrei faced a tense immigration interview and suspected that someone in Elizabeth’s family had reported him. The couple also prepared to welcome another child while setting firmer boundaries with the Potthasts.',
        partnerPersonIds: ['p-andrei-castravet'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
      {
        show: HEA,
        season: 9,
        title: 'Florida friendships get complicated',
        recap: 'Elizabeth and Andrei welcomed Jovi and Yara into their Florida circle, but clashing personalities and a secret shared among friends created new tension. Their marriage remained intact while the group chat metaphorically caught fire.',
        partnerPersonIds: ['p-andrei-castravet'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
    ],
  },
  'p-kara-bass': {
    connections: [
      {
        name: 'Guillermo Rojer',
        personId: 'p-guillermo-rojer',
        relationshipId: 'ARC-6FC42EB29A8D',
        context: 'The Venezuelan man she met in the Dominican Republic, later married and welcomed a child with.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 9,
        title: 'A Dominican Republic romance comes home',
        recap: 'Guillermo moved to Virginia on a K-1 visa after the pandemic separated him from Kara. They argued over money, control and their age difference, then married before the deadline.',
        partnerPersonIds: ['p-guillermo-rojer'],
        relationshipId: 'ARC-6FC42EB29A8D',
      },
      {
        show: HEA,
        season: 9,
        title: 'New parents lose the spark',
        recap: 'Life with a baby left Kara and Guillermo struggling to find time and intimacy as a couple. A confession from Kara and the pressure of rebuilding trust pushed them toward one last attempt to reconnect.',
        partnerPersonIds: ['p-guillermo-rojer'],
        relationshipId: 'ARC-6FC42EB29A8D',
      },
      {
        show: LAST_RESORT,
        season: 3,
        title: 'Separate homes, shared history',
        recap: 'Kara and Guillermo entered the castle living minutes apart in separate homes after jealousy, resentment and suspicions of infidelity overwhelmed the marriage. Therapy showed that the feelings remained, even when a workable path forward did not.',
        partnerPersonIds: ['p-guillermo-rojer'],
        relationshipId: 'ARC-6FC42EB29A8D',
      },
      {
        show: HUNT_FOR_LOVE,
        season: 2,
        title: 'A single chapter in Puerto Rico',
        recap: 'TLC announced Kara as one of seven returning singles heading to Puerto Rico to meet new people and find someone who accepts her as she is. This chapter is announced and has not premiered yet.',
        status: 'announced',
      },
    ],
  },
  'p-ed-brown': {
    connections: [
      {
        name: 'Rosemarie Vega',
        personId: 'p-rosemarie-vega',
        relationshipId: 'ARC-7966A39D3E56',
        context: 'His original online girlfriend and the reason for his first trip to the Philippines.',
      },
      {
        name: 'Liz Woods',
        personId: 'p-liz-woods',
        relationshipId: 'ARC-257C9F77A6EF',
        context: 'The restaurant manager he later dated, broke up with, reconciled with and became engaged to.',
      },
    ],
    seasons: [
      {
        show: B90,
        season: 4,
        title: 'Meeting Rose in the Philippines',
        recap: 'Ed traveled to meet Rose and her son after building their relationship online. Questions about trust, respect and future children ended the romance before the proposal he had imagined.',
        partnerPersonIds: ['p-rosemarie-vega'],
        relationshipId: 'ARC-7966A39D3E56',
      },
      {
        show: SINGLE_LIFE,
        season: 1,
        title: 'Liz enters the story',
        recap: 'Back in San Diego, Ed asked out Liz, a manager at a restaurant he visited regularly. Their connection moved quickly through first dates, family introductions and a painful breakup.',
        partnerPersonIds: ['p-liz-woods'],
        relationshipId: 'ARC-257C9F77A6EF',
      },
      {
        show: SINGLE_LIFE,
        season: 2,
        title: 'Dating detours and a surprise engagement',
        recap: 'Ed tried dating again, including a meaningful connection with Kaory in Mexico, before reuniting with Liz. By the Tell All, Ed and Liz revealed that they were engaged.',
        partnerPersonIds: ['p-liz-woods'],
        relationshipId: 'ARC-257C9F77A6EF',
      },
      {
        show: HEA,
        season: 7,
        title: 'Engaged, but rarely at peace',
        recap: 'Ed and Liz attempted to plan a future while jealousy, drinking and repeated breakups destabilized the engagement. Their Tell All arguments confirmed that “together” remained a highly flexible term.',
        partnerPersonIds: ['p-liz-woods'],
        relationshipId: 'ARC-257C9F77A6EF',
      },
      {
        show: LAST_RESORT,
        season: 1,
        title: 'Therapy and a recommitment',
        recap: 'The couple arrived at the retreat hoping intensive therapy could stop their breakup cycle. They left recommitted and still engaged, carrying the relationship into another season.',
        partnerPersonIds: ['p-liz-woods'],
        relationshipId: 'ARC-257C9F77A6EF',
      },
      {
        show: HEA,
        season: 8,
        title: 'The wedding is canceled',
        recap: 'A family argument became the final rupture, and Ed canceled the wedding without first telling Liz the relationship was over. A later coffee meeting gave them closure instead of another reconciliation.',
        partnerPersonIds: ['p-liz-woods'],
        relationshipId: 'ARC-257C9F77A6EF',
      },
    ],
  },
  'p-michael-ilesanmi': {
    connections: [
      {
        name: 'Angela Deem',
        personId: 'p-angela-deem',
        relationshipId: 'ARC-51D2491498E4',
        context: 'The Georgia woman he met online, later married in Nigeria and eventually joined in the United States.',
      },
    ],
    seasons: [
      {
        show: B90,
        season: 2,
        title: 'Angela travels to Nigeria',
        recap: 'After Michael messaged Angela online, she flew from Georgia to Nigeria for their first meeting. Their age gap, trust problems and different expectations caused fireworks, but Angela still accepted his proposal.',
        partnerPersonIds: ['p-angela-deem'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: B90,
        season: 3,
        title: 'A visa plan meets family pressure',
        recap: 'Angela returned to Nigeria as the couple debated marriage, children and how to satisfy both families. The K-1 plan continued despite recurring accusations and arguments.',
        partnerPersonIds: ['p-angela-deem'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: MAIN,
        season: 7,
        title: 'A K-1 denial changes the route',
        recap: 'Michael and Angela tried to maintain the engagement from opposite continents while waiting on immigration. When the K-1 visa was denied, marriage in Nigeria became their clearest remaining path.',
        partnerPersonIds: ['p-angela-deem'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: HEA,
        season: 5,
        title: 'The Nigerian wedding',
        recap: 'Angela returned to Nigeria and married Michael while worrying about family, health and the realities of a spousal visa. The ceremony made the relationship official even as the distance remained.',
        partnerPersonIds: ['p-angela-deem'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: HEA,
        season: 6,
        title: 'A long-distance marriage frays',
        recap: 'Years apart amplified fights about fertility, money and Angela’s medical procedures. Their calls increasingly became confrontations rather than a bridge between countries.',
        partnerPersonIds: ['p-angela-deem'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: HEA,
        season: 7,
        title: 'Social media becomes the battleground',
        recap: 'Angela suspected Michael’s new social-media presence was hiding infidelity and confronted him during an unannounced Nigeria trip. The season ended with another attempted reconciliation and more unanswered questions.',
        partnerPersonIds: ['p-angela-deem'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: LAST_RESORT,
        season: 1,
        title: 'Remote-controlled couples therapy',
        recap: 'Visa delays kept Michael on a screen while Angela attended the retreat in person. They worked through a cheating admission and recommitted, making theirs the only therapy room that required dependable Wi-Fi.',
        partnerPersonIds: ['p-angela-deem'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: HEA,
        season: 8,
        title: 'Michael finally reaches America',
        recap: 'The spousal visa was approved and Michael moved to Georgia, ending years of long distance. Their reunion unraveled quickly, and he left Angela’s home after only a short time in the United States.',
        partnerPersonIds: ['p-angela-deem'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: HUNT_FOR_LOVE,
        season: 2,
        title: 'A newly divorced single starts over',
        recap: 'TLC announced Michael as a returning single in Puerto Rico, looking for romance and a genuine best friend after divorce. The season is announced and has not premiered yet.',
        status: 'announced',
      },
    ],
  },
  'p-guillermo-rojer': {
    connections: [
      {
        name: 'Kara Bass',
        personId: 'p-kara-bass',
        relationshipId: 'ARC-6FC42EB29A8D',
        context: 'The woman he met in the Dominican Republic, later married and welcomed a child with in Virginia.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 9,
        title: 'A Dominican Republic romance comes home',
        recap: 'Guillermo moved to Virginia on a K-1 visa after the pandemic separated him from Kara. They argued over money, control and their age difference, then married before the deadline.',
        partnerPersonIds: ['p-kara-bass'],
        relationshipId: 'ARC-6FC42EB29A8D',
      },
      {
        show: HEA,
        season: 9,
        title: 'New parents lose the spark',
        recap: 'Life with a baby left Kara and Guillermo struggling to find time and intimacy as a couple. A confession from Kara and the pressure of rebuilding trust pushed them toward one last attempt to reconnect.',
        partnerPersonIds: ['p-kara-bass'],
        relationshipId: 'ARC-6FC42EB29A8D',
      },
      {
        show: LAST_RESORT,
        season: 3,
        title: 'Separate homes, shared history',
        recap: 'Kara and Guillermo entered the castle living minutes apart in separate homes after jealousy, resentment and suspicions of infidelity overwhelmed the marriage. Therapy showed that the feelings remained, even when a workable path forward did not.',
        partnerPersonIds: ['p-kara-bass'],
        relationshipId: 'ARC-6FC42EB29A8D',
      },
    ],
  },
  'p-gino-palazzolo': {
    connections: [
      {
        name: 'Jasmine Pineda',
        personId: 'p-jasmine-pineda',
        relationshipId: 'ARC-B09532768188',
        context: 'His original online match from Panama, later fiancée and wife, now ex-wife following their confirmed June 2026 divorce.',
      },
      {
        name: 'Natalie Mordovtseva',
        personId: 'p-natalie-mordovtseva',
        context: 'The former flame Gino reconnected with while dating again on The Single Life Season 5.',
      },
    ],
    seasons: [
      {
        show: B90,
        season: 5,
        title: 'First meeting in Panama',
        recap: 'After eight months online, Gino traveled to Panama to meet Jasmine in person. Jealousy, money and trust collided almost immediately, but the trip still ended with a proposal.',
        partnerPersonIds: ['p-jasmine-pineda'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: B90,
        season: 6,
        title: 'An engagement under pressure',
        recap: 'The couple tried to prepare for marriage while arguing over a prenup, finances and old relationships. Their K-1 plans moved forward, even when their trust seemed to be moving in the opposite direction.',
        partnerPersonIds: ['p-jasmine-pineda'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: MAIN,
        season: 10,
        title: 'Panama to Michigan',
        recap: 'Jasmine arrived in Michigan for the 90-day countdown and discovered that a new country did not magically retire their old arguments. They nevertheless reached the altar and married.',
        partnerPersonIds: ['p-jasmine-pineda'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: HEA,
        season: 8,
        title: 'Marriage, paperwork and a pageant',
        recap: 'Married life brought fights over intimacy, money and the immigration process for Jasmine’s sons. A beauty-pageant storyline provided sparkle, but not a solution to the larger problems.',
        partnerPersonIds: ['p-jasmine-pineda'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: LAST_RESORT,
        season: 2,
        title: 'One last retreat—and an open door',
        recap: 'Gino and Jasmine entered couples therapy with their marriage in serious trouble. The retreat ended with an open-marriage experiment that brought Matt into the story and ultimately did not save the marriage.',
        partnerPersonIds: ['p-jasmine-pineda'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: HEA,
        season: 9,
        title: 'The open marriage changes everything',
        recap: 'Back home, Gino and Jasmine discovered that additional partners made their already-fragile marriage even harder to manage. Jasmine’s relationship with Matt became a new family chapter while she and Gino moved toward divorce.',
        partnerPersonIds: ['p-jasmine-pineda'],
        relationshipId: 'ARC-B09532768188',
      },
      {
        show: SINGLE_LIFE,
        season: 5,
        title: 'Dating again, with unfinished paperwork',
        recap: 'Newly separated, Gino swapped couples therapy for dating apps and a second chance with former flame Natalie. Their stop-start romance was complicated by his unfinished divorce and enough awkward honesty to make the Tell All earn its name.',
        partnerPersonIds: ['p-natalie-mordovtseva'],
      },
    ],
  },
  'p-jovi-dufren': {
    connections: [
      {
        name: 'Yara Zaya',
        personId: 'p-yara-zaya',
        relationshipId: 'ARC-55676CA1337A',
        context: 'His travel-app match, wife and co-parent throughout his franchise story.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 8,
        title: 'A K-1 move to New Orleans',
        recap: 'Yara moved from Kyiv to Louisiana and quickly learned that Jovi’s work travel and nightlife would be recurring cast members. A surprise pregnancy accelerated the wedding plans and the start of their family.',
        partnerPersonIds: ['p-yara-zaya'],
        relationshipId: 'ARC-55676CA1337A',
      },
      {
        show: HEA,
        season: 6,
        title: 'New parents, familiar disagreements',
        recap: 'With baby Mylah at the center of their lives, Yara wanted stability while Jovi struggled to retire his bachelor habits. Work absences and one especially unhelpful strip-club outing kept the peace elusive.',
        partnerPersonIds: ['p-yara-zaya'],
        relationshipId: 'ARC-55676CA1337A',
      },
      {
        show: HEA,
        season: 7,
        title: 'Europe enters the family plan',
        recap: 'A trip to Europe during the war in Ukraine intensified Yara’s homesickness and her wish for a European home base. Jovi worried that a second residence could become a second life without him.',
        partnerPersonIds: ['p-yara-zaya'],
        relationshipId: 'ARC-55676CA1337A',
      },
      {
        show: LAST_RESORT,
        season: 1,
        title: 'The baby question reaches therapy',
        recap: 'The couple entered the retreat divided over trust, nightlife and whether to have another child. Yara’s private use of birth control became a major conversation about autonomy and communication.',
        partnerPersonIds: ['p-yara-zaya'],
        relationshipId: 'ARC-55676CA1337A',
      },
      {
        show: HEA,
        season: 9,
        title: 'A Miami move and new friendships',
        recap: 'Jovi and Yara relocated to Miami with Mylah and tried to turn the change into a fresh start. Their new social circle with other franchise couples brought support, friction and several opinions nobody had ordered.',
        partnerPersonIds: ['p-yara-zaya'],
        relationshipId: 'ARC-55676CA1337A',
      },
    ],
  },
  'p-russ-mayfield': {
    connections: [
      {
        name: 'Paola Mayfield',
        personId: 'p-paola-mayfield',
        relationshipId: 'ARC-6C7FAE6C41EF',
        context: 'The Colombian woman he met through work, later married and built a family with in the United States.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 1,
        title: 'Colombia meets Oklahoma',
        recap: 'Pao arrived on a K-1 visa and tried to picture a future in Russ’s conservative Oklahoma hometown. The couple made it through culture shock and family concerns to become one of the franchise’s original marriages.',
        partnerPersonIds: ['p-paola-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
      {
        show: HEA,
        season: 1,
        title: 'Miami calls',
        recap: 'Pao pursued modeling opportunities in Miami while Russ worried about the jobs, photoshoots and distance involved. Their first post-wedding season established geography as a permanent third wheel.',
        partnerPersonIds: ['p-paola-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
      {
        show: HEA,
        season: 2,
        title: 'Career dreams versus comfort zones',
        recap: 'The move toward Miami gave Pao more room to chase her ambitions and Russ more reasons to feel displaced. They tried to support each other while disagreeing over work, friends and boundaries.',
        partnerPersonIds: ['p-paola-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
      {
        show: HEA,
        season: 3,
        title: 'Grief changes the conversation',
        recap: 'After suffering a miscarriage, Pao and Russ worked through grief alongside their familiar communication problems. The season shifted their family plans from an abstract debate to an emotional priority.',
        partnerPersonIds: ['p-paola-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
      {
        show: HEA,
        season: 4,
        title: 'Becoming parents',
        recap: 'Pregnancy and the birth of their son Axel opened a joyful new chapter, but family boundaries and differing expectations followed them into parenthood. Even the baby shower came with a side of tension.',
        partnerPersonIds: ['p-paola-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
      {
        show: LAST_RESORT,
        season: 3,
        title: 'A marriage living in two places',
        recap: 'Pao’s wrestling career took her to Las Vegas while Russ resisted being left behind, widening a separation that was already emotional. At the castle, old resentments and painful revelations forced them to ask whether the marriage could continue.',
        partnerPersonIds: ['p-paola-mayfield'],
        relationshipId: 'ARC-6C7FAE6C41EF',
      },
    ],
  },
  'p-andrei-castravet': {
    connections: [
      {
        name: 'Elizabeth Potthast',
        personId: 'p-elizabeth-potthast',
        relationshipId: 'ARC-7A6D723EF5C6',
        context: 'The Florida woman he met in Dublin, later married and built a family and business with.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 5,
        title: 'Dublin romance, Florida reality',
        recap: 'Andrei moved to Florida on a K-1 visa after meeting Elizabeth in Dublin. Her family questioned his plans and personality throughout the countdown, but the couple married.',
        partnerPersonIds: ['p-elizabeth-potthast'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
      {
        show: HEA,
        season: 4,
        title: 'Marriage meets the family business',
        recap: 'Money, work and Andrei’s relationship with Elizabeth’s relatives dominated married life as they prepared for their first child. Everyone had a job opinion, including several people who were not hiring.',
        partnerPersonIds: ['p-elizabeth-potthast'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
      {
        show: HEA,
        season: 5,
        title: 'A second wedding in Moldova',
        recap: 'The couple planned a traditional Moldovan wedding while Elizabeth’s family investigated Andrei’s past and clashed with his culture. The celebration happened, but diplomacy did not attend every event.',
        partnerPersonIds: ['p-elizabeth-potthast'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
      {
        show: HEA,
        season: 6,
        title: 'Real estate becomes a family sport',
        recap: 'Andrei joined Chuck’s real-estate business, inflaming old tensions with Elizabeth’s siblings over money and opportunity. A family cookout proved that property was not the only thing capable of flipping.',
        partnerPersonIds: ['p-elizabeth-potthast'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
      {
        show: HEA,
        season: 7,
        title: 'Immigration questions and family suspicion',
        recap: 'Andrei faced a tense immigration interview and suspected that someone in Elizabeth’s family had reported him. The couple also prepared to welcome another child while setting firmer boundaries with the Potthasts.',
        partnerPersonIds: ['p-elizabeth-potthast'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
      {
        show: HEA,
        season: 9,
        title: 'Florida friendships get complicated',
        recap: 'Elizabeth and Andrei welcomed Jovi and Yara into their Florida circle, but clashing personalities and a secret shared among friends created new tension. Their marriage remained intact while the group chat metaphorically caught fire.',
        partnerPersonIds: ['p-elizabeth-potthast'],
        relationshipId: 'ARC-7A6D723EF5C6',
      },
    ],
  },
  'p-angela-deem': {
    connections: [
      {
        name: 'Michael Ilesanmi',
        personId: 'p-michael-ilesanmi',
        relationshipId: 'ARC-51D2491498E4',
        context: 'The Nigerian man she met online, later married and eventually welcomed to the United States.',
      },
    ],
    seasons: [
      {
        show: B90,
        season: 2,
        title: 'Angela travels to Nigeria',
        recap: 'After Michael messaged Angela online, she flew from Georgia to Nigeria for their first meeting. Their age gap, trust problems and different expectations caused fireworks, but Angela still accepted his proposal.',
        partnerPersonIds: ['p-michael-ilesanmi'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: B90,
        season: 3,
        title: 'A visa plan meets family pressure',
        recap: 'Angela returned to Nigeria as the couple debated marriage, children and how to satisfy both families. The K-1 plan continued despite recurring accusations and arguments.',
        partnerPersonIds: ['p-michael-ilesanmi'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: MAIN,
        season: 7,
        title: 'A K-1 denial changes the route',
        recap: 'Michael and Angela tried to maintain the engagement from opposite continents while waiting on immigration. When the K-1 visa was denied, marriage in Nigeria became their clearest remaining path.',
        partnerPersonIds: ['p-michael-ilesanmi'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: HEA,
        season: 5,
        title: 'The Nigerian wedding',
        recap: 'Angela returned to Nigeria and married Michael while worrying about family, health and the realities of a spousal visa. The ceremony made the relationship official even as the distance remained.',
        partnerPersonIds: ['p-michael-ilesanmi'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: HEA,
        season: 6,
        title: 'A long-distance marriage frays',
        recap: 'Years apart amplified fights about fertility, money and Angela’s medical procedures. Their calls increasingly became confrontations rather than a bridge between countries.',
        partnerPersonIds: ['p-michael-ilesanmi'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: HEA,
        season: 7,
        title: 'Social media becomes the battleground',
        recap: 'Angela suspected Michael’s new social-media presence was hiding infidelity and confronted him during an unannounced Nigeria trip. The season ended with another attempted reconciliation and more unanswered questions.',
        partnerPersonIds: ['p-michael-ilesanmi'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: LAST_RESORT,
        season: 1,
        title: 'Remote-controlled couples therapy',
        recap: 'Visa delays kept Michael on a screen while Angela attended the retreat in person. They worked through a cheating admission and recommitted, making theirs the only therapy room that required dependable Wi-Fi.',
        partnerPersonIds: ['p-michael-ilesanmi'],
        relationshipId: 'ARC-51D2491498E4',
      },
      {
        show: HEA,
        season: 8,
        title: 'Michael finally reaches America',
        recap: 'The spousal visa was approved and Michael moved to Georgia, ending years of long distance. Their reunion unraveled quickly, and he left Angela’s home after only a short time in the United States.',
        partnerPersonIds: ['p-michael-ilesanmi'],
        relationshipId: 'ARC-51D2491498E4',
      },
    ],
  },
  'p-biniyam-shibre': {
    connections: [
      {
        name: 'Ariela Weinberg',
        personId: 'p-ariela-weinberg',
        relationshipId: 'ARC-84F46727A2A5',
        context: 'The American traveler he met in Ethiopia and later built an international family with.',
      },
    ],
    seasons: [
      {
        show: TOW,
        season: 2,
        title: 'A baby and a shared life in Ethiopia',
        recap: 'Pregnant Ari returned to Ethiopia to give birth near Biniyam and attempt a shared life. Housing, religion and trust became immediate tests as they welcomed their son Avi.',
        partnerPersonIds: ['p-ariela-weinberg'],
        relationshipId: 'ARC-84F46727A2A5',
      },
      {
        show: TOW,
        season: 3,
        title: 'Distance tests the engagement',
        recap: 'Ari brought Avi to the United States for surgery while Bini remained in Ethiopia. Reports of parties and unanswered calls deepened their trust problems before the couple pursued a move to America.',
        partnerPersonIds: ['p-ariela-weinberg'],
        relationshipId: 'ARC-84F46727A2A5',
      },
      {
        show: MAIN,
        season: 9,
        title: 'Starting over in New Jersey',
        recap: 'Biniyam arrived in the United States and chased an MMA dream while Ari worried about finances and stability. Ari’s ex-husband Leandro remained in their orbit, but Ari and Bini reached the wedding.',
        partnerPersonIds: ['p-ariela-weinberg'],
        relationshipId: 'ARC-84F46727A2A5',
      },
      {
        show: LAST_RESORT,
        season: 2,
        title: 'A separated couple tries therapy',
        recap: 'Ari and Bini arrived separately with their marriage already deeply fractured. The retreat forced a painful conversation about trust, distance and whether divorce and co-parenting were now the healthiest path.',
        partnerPersonIds: ['p-ariela-weinberg'],
        relationshipId: 'ARC-84F46727A2A5',
      },
    ],
  },
  'p-rob-warne': {
    connections: [
      {
        name: 'Sophie Sierra',
        personId: 'p-sophie-sierra',
        relationshipId: 'ARC-6E9479AA5D80',
        context: 'His original social-media match, fiancée and wife after she moved from London to Los Angeles.',
      },
      {
        name: 'Elise Benson',
        personId: 'p-elise-benson',
        relationshipId: 'ARC-6CB6773F8F74',
        context: 'The early connection he explored after arriving at the Hunt for Love retreat.',
      },
      {
        name: 'Jeniffer Tarazona',
        personId: 'p-jeniffer-tarazona',
        relationshipId: 'ARC-4A522D4B0E29',
        context: 'The later retreat connection that placed Rob inside the season’s central love triangle.',
      },
    ],
    seasons: [
      {
        show: MAIN,
        season: 10,
        title: 'A very unglamorous Los Angeles landing',
        recap: 'Sophie moved from London to marry Rob and quickly found that his Los Angeles setup did not match the life she imagined. Online cheating, money and trust followed them to the altar anyway.',
        partnerPersonIds: ['p-sophie-sierra'],
        relationshipId: 'ARC-6E9479AA5D80',
      },
      {
        show: HEA,
        season: 8,
        title: 'Married, but mostly apart',
        recap: 'Sophie spent much of the season living away from Rob while they tried to repair trust and intimacy. Therapy and increasingly creative date ideas could not settle the larger question of whether the marriage was working.',
        partnerPersonIds: ['p-sophie-sierra'],
        relationshipId: 'ARC-6E9479AA5D80',
      },
      {
        show: LAST_RESORT,
        season: 2,
        title: 'The retreat becomes the ending',
        recap: 'Rob and Sophie entered therapy with years of hurt and suspicion already packed in their bags. They briefly reconnected after the retreat, but ultimately ended the relationship for good.',
        partnerPersonIds: ['p-sophie-sierra'],
        relationshipId: 'ARC-6E9479AA5D80',
      },
      {
        show: HUNT_FOR_LOVE,
        season: 1,
        title: 'A fresh start becomes a love triangle',
        recap: 'Newly single, Rob joined the Tulum retreat, connected first with Elise and then pursued Jeniffer as her bond with Cole kept the triangle alive. By the finale, the attraction was clear while a long-term label remained very much under review.',
        partnerPersonIds: ['p-elise-benson', 'p-jeniffer-tarazona'],
      },
    ],
  },
}

export const castProfileTimelines: Record<string, CastProfileTimeline> = {
  ...coreCastProfileTimelines,
  ...batch50Timelines,
  ...nextBatch50Timelines,
  ...researchedTimelinesB,
  ...researchedTimelinesA,
  ...researchedTimelinesC,
  ...legacyTimelinesB,
  ...legacyTimelinesA,
  ...legacyTimelinesC,
  ...researchedTimelinesCFollowup,
  ...finalCarmellaTimelines,
  ...finalIdentityTimelines,
  ...finalHuntTimelinesCE,
  ...finalHuntTimelinesCT,
  ...familyChantelCanonTimelines,
  ...darceyStaceyCanonTimelines,
}

for (const [personId, chapters] of Object.entries({ ...familyChantelSeasonAdditions, ...darceyStaceySeasonAdditions })) {
  const timeline = castProfileTimelines[personId]
  if (timeline) timeline.seasons.push(...chapters.filter((chapter) => !timeline.seasons.some((existing) => existing.show === chapter.show && existing.season === chapter.season)))
}

// Make new cast reachable from the established profiles too. Origins remain
// unchanged; a later romantic arc never replaces the relationship of entry.
for (const [personId, timeline] of Object.entries({ ...familyChantelCanonTimelines, ...darceyStaceyCanonTimelines })) {
  for (const connection of [...timeline.connections]) {
    if (!connection.personId) continue
    const target = castProfileTimelines[connection.personId]
    if (!target || target.connections.some((existing) => existing.personId === personId)) continue
    const link: CastConnection = {
      name: canonCastNames[personId], personId, relationshipId: connection.relationshipId,
      context: `Connected through ${timeline.seasons[0]?.show ?? 'the franchise'}. Explore their own family, friendship or relationship chapter.`,
    }
    // Upgrade the earlier unlinked Georgi entry instead of duplicating it.
    const pending = target.connections.findIndex((existing) => !existing.personId && personId === 'p-georgi-rusev' && /Georgi/i.test(existing.name))
    if (pending >= 0) target.connections[pending] = { ...link, context: target.connections[pending].context }
    else target.connections.push(link)
  }
}

for (const [personId, origin] of Object.entries(editorialOrigins)) {
  if (castProfileTimelines[personId]) castProfileTimelines[personId] = { ...castProfileTimelines[personId], origin }
}

for (const [personId, partnerId] of [['p-stacey-silva', 'p-florian-sukaj'], ['p-florian-sukaj', 'p-stacey-silva']]) {
  const partner = castProfileTimelines[personId]?.connections.find(connection => connection.personId === partnerId)
  if (partner) partner.context = 'Their social-media connection and long-distance engagement preceded the move and quarantine wedding in Darcey & Stacey Season 1. The Last Resort is a later therapy chapter.'
}

export function timelineForPerson(personId: string) {
  return castProfileTimelines[personId]
}
